import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { animationFrameScheduler, Subject, Subscription } from 'rxjs';
import { auditTime, debounceTime, skip, tap } from 'rxjs/operators';

import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../../../common/take-until-destroy.decorator';
import { useDispatcher } from '../../../../../store.decorator';
import { useSelector } from '../../../../store-use-selector';
import { DateRange } from '../../model/date-range.model';
import { OrderBy } from '../../model/order-by.model';
import { Status } from '../../model/status.model';
import { filterItems } from '../../state/filter-items.action';
import { sortItems } from '../../state/sort-items.action';
import { toggleSearch } from '../../state/toggle-search.action';
import { TaskListDialogService } from '../../task-list-dialog.service';
import { TaskListDialogState } from '../../task-list-dialog.state';

import { TranslationKeyMap } from './search-filter.component.i18n';

@Component({
    selector: 'cmps-task-list-search-filter',
    templateUrl: './search-filter.component.html',
    host: {
        class: 'cmps-task-list-search-filter',
        '[class.cmps-task-list-search-filter--open]':
            '_service.state?.searchOpen',
    },
})
@TakeUntilDestroy()
export class SearchFilterComponent implements OnInit, OnDestroy {
    /** @private */
    _i18n = TranslationKeyMap;

    /** @private */
    _OrderBy = OrderBy;

    private _keywordSubj: Subject<string>;
    private _keywordSub: Subscription;

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        /** @private */ readonly _service: TaskListDialogService,
    ) {}

    ngOnInit(): void {
        this._initKeyword();
        this._initSearchInputFocus();
    }

    ngOnDestroy(): void {
        this._keywordSub?.unsubscribe();
        this._keywordSubj?.complete();
    }

    /** @private */
    _onToggleSearchClick(): void {
        if (this._service.state?.searchOpen === true) {
            this._dispatchFilterKeyword('');
        }
        useDispatcher(this._service, toggleSearch({}));
    }

    /** @private */
    _onKeywordFilterChange(value: string): void {
        this._keywordSubj.next(value);
    }

    /** @private */
    _onImportanceFilterClick(): void {
        const status: Status = {
            important: this._service.state.filterOpts.status.important !== true,
        };
        useDispatcher(
            this._service,
            filterItems({
                opts: {
                    ...this._service.state.filterOpts,
                    status,
                },
            }),
        );
    }

    /** @private */
    _onDateRangeFilterChange(dateRange: DateRange): void {
        useDispatcher(
            this._service,
            filterItems({
                opts: {
                    ...this._service.state.filterOpts,
                    dateRange,
                },
            }),
        );
    }

    /** @private */
    _onClearDateRangeClick(): void {
        useDispatcher(
            this._service,
            filterItems({
                opts: {
                    ...this._service.state.filterOpts,
                    dateRange: {
                        from: null,
                        to: null,
                    },
                },
            }),
        );
    }

    /** @private */
    _onOrderByChange(orderBy: OrderBy): void {
        useDispatcher(this._service, sortItems({ orderBy }));
    }

    /** @private */
    _dispatchFilterKeyword(keyword: string): void {
        useDispatcher(
            this._service,
            filterItems({
                opts: {
                    ...this._service.state.filterOpts,
                    keyword,
                },
            }),
        );
    }

    private _initKeyword(): void {
        this._keywordSubj = new Subject();
        this._keywordSub = this._keywordSubj
            .asObservable()
            .pipe(
                debounceTime(500),
                tap((keyword) => {
                    this._dispatchFilterKeyword(keyword);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _initSearchInputFocus(): void {
        useSelector<boolean>(
            (state) =>
                (state?.teamsCall?.taskList as TaskListDialogState)?.searchOpen,
        )
            .pipe(
                skip(1),
                auditTime(0, animationFrameScheduler),
                tap(([prevState, state]) => {
                    if (!prevState && state) {
                        this._el.nativeElement.querySelector('input')?.focus();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
